import { IonIcon, IonSpinner } from '@ionic/react';
import { helpCircleOutline, musicalNotesOutline } from 'ionicons/icons';
import React, { useMemo, useState } from 'react';
import { appendRunNotes } from '../../services/AgentChat.service';
import { createAgentMyCrate } from '../../services/AgentCatalog.service';
import { publishAgentTransfer } from '../../services/AgentTransferFeed.service';
import SpotifyService from '../../services/Spotify.service';
import { exportTracksToStreaming } from '../../services/StreamingExport.service';
import TidalService from '../../services/Tidal.service';
import { iTrack } from '../../types/ITrack';
import { SERVICE_NAMES, StreamingServiceId } from '../../types/Streaming';
import { parseSongList } from './agentSongList';

/**
 * Song list from a screenshot or a typed crate request. Create Crate always
 * Magic Sorts into playable order. Buttons post the outcome into the thread.
 */

const MAX_LIST_ACTION_TRACKS = 200;
const MAX_MISSES_SHOWN = 5;

interface AgentSongListCardProps {
  /** Body of the ```songlist block, one Artist - Title per line. */
  raw: string;
  /** Section name from the fence line, e.g. "Must Play". */
  label?: string;
  /** Conversation the action outcomes are posted into. */
  runId?: string;
  /** True while the reply is still streaming, so actions wait for the full list. */
  busy?: boolean;
}

export const AgentSongListCard: React.FC<AgentSongListCardProps> = ({
  raw,
  label,
  runId,
  busy = false,
}) => {
  const songs = useMemo(() => parseSongList(raw), [raw]);
  const [working, setWorking] = useState<string | null>(null);
  const [progress, setProgress] = useState('');

  const listTitle = useMemo(() => {
    const day = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${label || 'Song list'} · ${day}`;
  }, [label]);

  if (!songs.length) return null;

  /** Agent-note markdown describing the saved crate, with a working link. */
  const saveCrate = async (): Promise<string> => {
    const result = await createAgentMyCrate({
      title: listTitle,
      description: 'Created from a song list in Ask Crate Hackers',
      tracks: songs.map((song) => ({ artist: song.artist, title: song.title })),
      mixableOrder: true,
    });
    if (!('id' in result) || !result.id) {
      throw new Error(
        ('message' in result && result.message) || 'Could not save the crate.',
      );
    }
    const count =
      result.trackCount === songs.length
        ? `${result.trackCount} tracks`
        : `${result.trackCount} of ${songs.length} tracks`;
    return `Saved [${result.title}](ch://mine/${result.id}): ${count} in playable order.`;
  };

  const exportList = async (service: StreamingServiceId): Promise<string> => {
    const connected =
      service === 'spotify'
        ? !!SpotifyService.getAccessToken()
        : TidalService.isConnected();
    if (!connected) {
      throw new Error(
        `${SERVICE_NAMES[service]} is not connected. Add it from the @ button below.`,
      );
    }
    const tracks = songs.map(
      (song, index) =>
        ({
          ID: `songlist-${index}`,
          Artist: song.artist,
          Title: song.title,
        }) as iTrack,
    );
    const result = await exportTracksToStreaming(
      { service, playlistName: listTitle, tracks },
      (report) => {
        publishAgentTransfer(report);
        setProgress(report.message);
      },
    );
    let note = `Added ${result.matched.length} of ${songs.length} to "${listTitle}" on ${SERVICE_NAMES[service]}.`;
    if (result.unmatched.length) {
      const misses = result.unmatched
        .slice(0, MAX_MISSES_SHOWN)
        .map((miss) =>
          miss.artist ? `${miss.artist} - ${miss.title}` : miss.title,
        )
        .join('; ');
      const more = result.unmatched.length - MAX_MISSES_SHOWN;
      note += ` Not found: ${misses}${more > 0 ? ` and ${more} more` : ''}.`;
    }
    return note;
  };

  const act = async (name: string, task: () => Promise<string>) => {
    if (working || !runId) return;
    setWorking(name);
    setProgress('');
    appendRunNotes(runId, [
      {
        role: 'user',
        content: `${name} - ${label || 'song list'} (${songs.length} songs)`,
      },
    ]);
    try {
      if (songs.length > MAX_LIST_ACTION_TRACKS) {
        throw new Error(
          `That is ${songs.length} songs — the limit is ${MAX_LIST_ACTION_TRACKS} per list.`,
        );
      }
      appendRunNotes(runId, [{ role: 'agent', content: await task() }]);
    } catch (error) {
      appendRunNotes(runId, [
        {
          role: 'agent',
          content: `${name} failed: ${
            error instanceof Error ? error.message : 'That did not work.'
          }`,
        },
      ]);
    } finally {
      setWorking(null);
      setProgress('');
    }
  };

  const actions: { name: string; task: () => Promise<string> }[] = [
    { name: 'Create Crate', task: saveCrate },
    { name: 'Export to Spotify', task: () => exportList('spotify') },
    { name: 'Export to Tidal', task: () => exportList('tidal') },
  ];

  return (
    <div className="agent-songlist-card">
      <div className="agent-songlist-header">
        <IonIcon icon={musicalNotesOutline} />
        <span>
          {label || 'Song list'} · {songs.length}{' '}
          {songs.length === 1 ? 'song' : 'songs'}
        </span>
      </div>
      <ol className="agent-songlist-tracks">
        {songs.map((song, index) => (
          <li key={`${song.artist}-${song.title}-${index}`}>
            {song.artist ? (
              <>
                <span className="agent-songlist-artist">{song.artist}</span>
                <span className="agent-songlist-title">{song.title}</span>
              </>
            ) : (
              <span className="agent-songlist-title">{song.title}</span>
            )}
            {song.uncertain && (
              <span
                className="agent-songlist-flag"
                title="Best reading of the image — double-check this one"
              >
                <IonIcon icon={helpCircleOutline} />
              </span>
            )}
          </li>
        ))}
      </ol>
      <div className="agent-songlist-actions">
        {actions.map((action) => (
          <button
            key={action.name}
            type="button"
            className="agent-songlist-action"
            disabled={busy || working !== null || !runId}
            onClick={() => act(action.name, action.task)}
          >
            {working === action.name && (
              <IonSpinner name="crescent" className="agent-songlist-spinner" />
            )}
            {action.name}
          </button>
        ))}
      </div>
      {working && (
        <div className="agent-songlist-status">
          <span>{progress || `${working}…`}</span>
        </div>
      )}
    </div>
  );
};

export default AgentSongListCard;
